import { useCurrentUser } from "@/contexts/user";
import { useConfigStore } from "@/store/config";
import { useTokenStore } from "@/store/token";
import {
  BulbOutlined,
  ClockCircleOutlined,
  FilePdfOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuOutlined,
  MoonOutlined,
  SettingOutlined,
  TranslationOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Layout as AntLayout,
  Button,
  Drawer,
  Dropdown,
  Grid,
  Segmented,
  Space,
  Switch,
  Typography,
  type MenuProps,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router";
const { Sider, Content } = AntLayout;

export default function ChatLayout() {
  const { t, i18n } = useTranslation();
  const { theme, setTheme, setLanguage } = useConfigStore();
  const { logout } = useTokenStore();
  const { isAdmin } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isDark = theme === "dark";
  const isMobile = !screens.md;
  const currentLanguage = useMemo(() => {
    const lang = i18n.language || "zh-CN";
    return lang.startsWith("en") ? "en" : "zh-CN";
  }, [i18n.language]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.theme = theme;
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  const handleLanguageChange = (value: string | number) => {
    const nextLang = value === "en" ? "en" : "zh-CN";
    i18n.changeLanguage(nextLang).then(() => {});
    setLanguage(nextLang);
  };

  const mobileMenuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "lang-zh-CN",
        icon: <TranslationOutlined />,
        label: `${t("language.chinese")}${currentLanguage === "zh-CN" ? " ✓" : ""}`,
      },
      {
        key: "lang-en",
        icon: <TranslationOutlined />,
        label: `${t("language.english")}${currentLanguage === "en" ? " ✓" : ""}`,
      },
      {
        type: "divider",
      },
      {
        key: "theme-light",
        icon: <BulbOutlined />,
        label: `${t("common.light")}${!isDark ? " ✓" : ""}`,
      },
      {
        key: "theme-dark",
        icon: <MoonOutlined />,
        label: `${t("common.dark")}${isDark ? " ✓" : ""}`,
      },
      {
        type: "divider",
      },
      {
        key: "logout",
        icon: <LogoutOutlined />,
        label: t("nav.logout"),
      },
    ],
    [currentLanguage, isDark, t],
  );

  const handleLogout = async () => {
    await logout();
    setMobileSidebarOpen(false);
    navigate("/login");
  };

  const handleMobileMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "lang-en") {
      handleLanguageChange("en");
      return;
    }
    if (key === "lang-zh-CN") {
      handleLanguageChange("zh-CN");
      return;
    }
    if (key === "theme-dark") {
      setTheme("dark");
      return;
    }
    if (key === "theme-light") {
      setTheme("light");
      return;
    }
    if (key === "logout") {
      handleLogout();
    }
  };

  const sidebarClassName = useMemo(
    () =>
      isDark
        ? "border-r border-white/10 text-slate-100"
        : "border-r border-slate-200 text-slate-900",
    [isDark],
  );

  const sidebarBody = (
    <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden px-4 py-5">
      <Space className="w-full min-w-0 justify-between">
        <Typography.Title
          level={4}
          className={isDark ? "m-0 text-white" : "m-0 text-slate-900"}
        >
          {t("app.title")}
        </Typography.Title>
        <Space size="small">
          {isAdmin && (
            <Button
              type="text"
              size="small"
              icon={<SettingOutlined />}
              onClick={() => navigate("/admin/users")}
              title={t("chatLayout.gotoAdmin") || "Go to Admin Panel"}
            />
          )}
        </Space>
      </Space>

      <Space orientation="vertical" size="small" className="mt-4 w-full">
        <Button
          type={location.pathname === "/create" ? "primary" : "text"}
          icon={<UploadOutlined />}
          onClick={() => {
            setMobileSidebarOpen(false);
            navigate("/create");
          }}
          block
          className="justify-start"
        >
          {t("nav.create")}
        </Button>
        <Button
          type={location.pathname === "/playground" ? "primary" : "text"}
          icon={<FilePdfOutlined />}
          onClick={() => {
            setMobileSidebarOpen(false);
            navigate("/playground");
          }}
          block
          className="justify-start"
        >
          {t("nav.playground")}
        </Button>
      </Space>

      <div
        className={
          isDark
            ? "mt-4 rounded-2xl border border-white/10 bg-white/5 p-3"
            : "mt-4 rounded-2xl border border-slate-200 bg-white/70 p-3"
        }
      >
        <Space orientation="vertical" size="small" className="w-full">
          <Space size="small" className="w-full justify-between">
            <Space size="small">
              <TranslationOutlined />
              <Typography.Text type="secondary">
                {t("language.english")}/{t("language.chinese")}
              </Typography.Text>
            </Space>
            <Segmented
              size="small"
              value={currentLanguage}
              options={[
                { label: t("language.chinese"), value: "zh-CN" },
                { label: t("language.english"), value: "en" },
              ]}
              onChange={handleLanguageChange}
            />
          </Space>
          <Space size="small" className="w-full justify-between">
            <Space size="small">
              {isDark ? <MoonOutlined /> : <BulbOutlined />}
              <Typography.Text type="secondary">
                {isDark ? t("common.dark") : t("common.light")}
              </Typography.Text>
            </Space>
            <Switch
              checked={isDark}
              onChange={(checked) => setTheme(checked ? "dark" : "light")}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<BulbOutlined />}
            />
          </Space>
        </Space>
      </div>

      <div className="min-h-0 flex-1" />

      <Button
        danger
        type="text"
        icon={<LogoutOutlined />}
        onClick={handleLogout}
        block
        className="justify-start"
      >
        {t("nav.logout")}
      </Button>

      <div
        className={
          isDark ? "brand-panel-subtle mt-4 p-3" : "brand-panel-subtle mt-4 p-3"
        }
      >
        <Space size="small" className="text-xs" align="center">
          <ClockCircleOutlined />
          <span>{t("uploadPattern.subtitle")}</span>
        </Space>
      </div>
    </div>
  );

  return (
    <AntLayout className="brand-layout-shell h-screen overflow-hidden">
      <div className="mx-auto flex h-screen w-full max-w-7xl overflow-hidden">
        {!isMobile ? (
          <Sider
            width={300}
            className={`${sidebarClassName} brand-panel-subtle m-3 h-[calc(100vh-1.5rem)] min-h-0 shrink-0 overflow-hidden rounded-3xl`}
            theme={isDark ? "dark" : "light"}
          >
            {sidebarBody}
          </Sider>
        ) : null}

        <Content className="min-w-0 flex-1 overflow-hidden px-3 py-3 md:px-5 md:py-5">
          {isMobile ? (
            <div className="mb-3 flex items-center justify-between gap-2 md:hidden">
              <Space size="small">
                <Button
                  icon={<MenuOutlined />}
                  onClick={() => setMobileSidebarOpen(true)}
                >
                  {t("nav.create")}
                </Button>
                <Dropdown
                  menu={{
                    items: mobileMenuItems,
                    onClick: handleMobileMenuClick,
                  }}
                  trigger={["click"]}
                >
                  <Button icon={<TranslationOutlined />} />
                </Dropdown>
              </Space>
              <Space size="small">
                <Switch
                  checked={isDark}
                  onChange={(checked) => setTheme(checked ? "dark" : "light")}
                  checkedChildren={<MoonOutlined />}
                  unCheckedChildren={<BulbOutlined />}
                />
                <Button
                  type="primary"
                  icon={<HomeOutlined />}
                  onClick={() => navigate("/create")}
                />
              </Space>
            </div>
          ) : null}

          <div
            className={
              isMobile
                ? "brand-panel flex h-[calc(100vh-5rem)] min-h-0 flex-col overflow-hidden p-4 md:p-6"
                : "brand-panel flex h-[calc(100vh-1.5rem)] min-h-0 flex-col overflow-hidden p-4 md:p-6"
            }
          >
            <div className="min-h-0 min-w-0 flex-1 overflow-hidden">
              <Outlet />
            </div>
          </div>
        </Content>
      </div>

      <Drawer
        title={t("nav.create")}
        placement="left"
        size={300}
        open={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        styles={{ body: { padding: 0 } }}
      >
        {sidebarBody}
      </Drawer>
    </AntLayout>
  );
}
